import React, { useCallback, useEffect, useState } from 'react';
import { GraduationCap, Hammer, Loader2, AlertTriangle, ClipboardCheck, Map, Users, FileText, Megaphone, PencilRuler, LayoutGrid, ShieldCheck, FolderOpen, HeartPulse, FileSignature, Receipt, Wallet, Boxes, FilePlus2 } from 'lucide-react';
import { ModuleTile, AngekuendigtTile, SectionHeading } from '../project-hub/ModuleTile';
import type { AccentKey } from '../project-hub/moduleAccents';
import {
    MeinUebungsprojekt,
    UEBUNGS_VORLAGEN,
    UebungNichtFreigeschaltet,
    UebungsVorlage,
    ladeMeineUebungsprojekte,
    uebungsprojektAnlegen,
    vorlageAusNummer,
} from '../../services/uebung/uebungService';
import type { AppView } from '../../types';

/**
 * ÜBUNGSPLATZ — die Kachelwand des Lernzentrums (Plan 8.1).
 *
 * Ein Klick legt das persönliche Übungsprojekt zur Vorlage an; ein zweiter
 * Klick öffnet das vorhandene. Welche der beiden Fälle es ist, entscheidet die
 * RPC und nicht diese Komponente — zwei schnelle Klicks dürfen nicht zwei
 * Projekte erzeugen.
 *
 * Eigenständig und ohne Kenntnis der Navigation: die Einbindung in AppView,
 * Sidebar und Sprung ins Projekt gehört Auftrag A3, deshalb genau ein
 * Rückkanal (`onOeffneProjekt`).
 *
 * Aussehen aus dem Projekt-Hub übernommen (ModuleTile/AngekuendigtTile), damit
 * der Übungsplatz aussieht wie das, was er übt — und damit die Farbtabelle
 * ACCENTS die einzige Stelle bleibt, an der Kachelfarben stehen.
 */

interface Props {
    /** Sprung in das angelegte Übungsprojekt. Setzt A3. */
    onOeffneProjekt: (projectId: string, view: AppView) => void;
}

/** Symbol und Farbe je Vorlage — dieselben Zuordnungen wie im Projekt-Hub. */
const OPTIK: Record<string, { icon: React.ReactNode; accent: AccentKey }> = {
    begehung:          { icon: <ClipboardCheck className="w-5 h-5 text-emerald-600" />, accent: 'emerald' },
    sigeplan:          { icon: <Map className="w-5 h-5 text-blue-600" />,               accent: 'blue' },
    einweisung:        { icon: <Users className="w-5 h-5 text-cyan-600" />,             accent: 'cyan' },
    unterlage:         { icon: <FileText className="w-5 h-5 text-violet-600" />,        accent: 'violet' },
    vorankuendigung:   { icon: <Megaphone className="w-5 h-5 text-amber-600" />,        accent: 'amber' },
    planungsphase:     { icon: <PencilRuler className="w-5 h-5 text-teal-600" />,       accent: 'teal' },
    asplan:            { icon: <LayoutGrid className="w-5 h-5 text-stone-600" />,       accent: 'stone' },
    baustellenordnung: { icon: <ShieldCheck className="w-5 h-5 text-fuchsia-600" />,    accent: 'fuchsia' },
    dokumente:         { icon: <FolderOpen className="w-5 h-5 text-indigo-600" />,      accent: 'indigo' },
    sifa:              { icon: <HeartPulse className="w-5 h-5 text-teal-600" />,        accent: 'teal' },
    angebot:           { icon: <FileSignature className="w-5 h-5 text-violet-600" />,   accent: 'violet' },
    rechnung:          { icon: <Receipt className="w-5 h-5 text-indigo-600" />,         accent: 'indigo' },
    buchhaltung:       { icon: <Wallet className="w-5 h-5 text-amber-600" />,           accent: 'amber' },
    lose:              { icon: <Boxes className="w-5 h-5 text-cyan-600" />,             accent: 'cyan' },
    leer:              { icon: <FilePlus2 className="w-5 h-5 text-stone-600" />,        accent: 'stone' },
};

const optikFuer = (id: string) => OPTIK[id] ?? { icon: <Hammer className="w-5 h-5 text-slate-600" />, accent: 'stone' as AccentKey };

export const Uebungsplatz: React.FC<Props> = ({ onOeffneProjekt }) => {
    const [meine, setMeine] = useState<MeinUebungsprojekt[]>([]);
    const [laden, setLaden] = useState(true);
    /** Vorlage, die gerade angelegt wird — sperrt genau ihre Kachel. */
    const [arbeitetAn, setArbeitetAn] = useState<string | null>(null);
    const [freigeschaltet, setFreigeschaltet] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);

    const ladeBestand = useCallback(async () => {
        setLaden(true);
        try {
            setMeine(await ladeMeineUebungsprojekte());
            setFreigeschaltet(true);
        } catch (e) {
            if (e instanceof UebungNichtFreigeschaltet) {
                setFreigeschaltet(false);
            } else {
                setFehler(e instanceof Error ? e.message : 'Die Übungsprojekte konnten nicht geladen werden.');
            }
        } finally {
            setLaden(false);
        }
    }, []);

    useEffect(() => { void ladeBestand(); }, [ladeBestand]);

    const vorhanden = (vorlageId: string): MeinUebungsprojekt | undefined =>
        meine.find((p) => (p.vorlage ?? vorlageAusNummer(p.project_number)) === vorlageId);

    const handleKlick = async (vorlage: UebungsVorlage) => {
        if (vorlage.geplant || arbeitetAn) return;
        setFehler(null);
        // Liegt das Projekt schon vor, wird es geöffnet statt neu angelegt —
        // sonst liefe jeder zweite Klick durch die RPC, nur um dieselbe id
        // zurückzubekommen.
        const da = vorhanden(vorlage.id);
        if (da) { onOeffneProjekt(da.id, vorlage.view); return; }
        setArbeitetAn(vorlage.id);
        try {
            const id = await uebungsprojektAnlegen(vorlage.id);
            await ladeBestand();
            onOeffneProjekt(id, vorlage.view);
        } catch (e) {
            if (e instanceof UebungNichtFreigeschaltet) setFreigeschaltet(false);
            else setFehler(e instanceof Error ? e.message : 'Das Übungsprojekt konnte nicht angelegt werden.');
        } finally {
            setArbeitetAn(null);
        }
    };

    return (
        <section className="space-y-4" data-tutorial="uebungsplatz">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
                <div className="bg-white border border-amber-200 w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                    <GraduationCap className="w-5 h-5 text-amber-600" />
                </div>
                <div className="min-w-0">
                    <h2 className="font-bold text-slate-800">Übungsplatz</h2>
                    <p className="text-[13px] text-slate-600 leading-relaxed mt-0.5">
                        Jede Vorlage legt ein eigenes Übungsprojekt für dich an — mit denselben Daten,
                        die du im Video siehst. Dort zählt nichts: keine Fälligkeit, keine Auswertung,
                        kein Versand, keine Rechnung. Kaputtmachen ist ausdrücklich erlaubt.
                    </p>
                </div>
            </div>

            {!freigeschaltet && (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-bold text-slate-700">Noch nicht freigeschaltet</p>
                        <p className="text-[12px] text-slate-500 leading-relaxed mt-0.5">
                            Der Übungsplatz braucht die Datenbank-Migration Nr. 100/101. Sobald sie in dieser
                            Umgebung gelaufen ist, erscheinen die Kacheln hier von selbst.
                        </p>
                    </div>
                </div>
            )}

            {fehler && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-[13px] text-red-700 leading-relaxed">{fehler}</p>
                </div>
            )}

            {laden ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm p-6">
                    <Loader2 className="w-4 h-4 animate-spin" /> Übungsprojekte werden geladen …
                </div>
            ) : (
                <>
                    <SectionHeading label="Zum Ausprobieren" hint="Ein Projekt je Vorlage und Nutzer" />
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {UEBUNGS_VORLAGEN.map((v) => {
                            const { icon, accent } = optikFuer(v.id);
                            if (v.geplant) {
                                return <AngekuendigtTile key={v.id} icon={icon} title={v.titel} subtitle={v.beschreibung} />;
                            }
                            const da = vorhanden(v.id);
                            const busy = arbeitetAn === v.id;
                            return (
                                <ModuleTile
                                    key={v.id}
                                    accent={accent}
                                    badge={da ? 'ANGELEGT' : 'ÜBUNG'}
                                    icon={busy ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : icon}
                                    title={v.titel}
                                    subtitle={v.beschreibung}
                                    cta={busy ? 'Wird angelegt …' : da ? 'Übungsprojekt öffnen' : 'Übungsprojekt anlegen'}
                                    onClick={() => { void handleKlick(v); }}
                                    lastActivity={da?.updated_at ?? null}
                                    className={busy || !freigeschaltet ? 'pointer-events-none opacity-60' : ''}
                                />
                            );
                        })}
                    </div>
                </>
            )}
        </section>
    );
};
